import { NavLink, useLocation } from 'react-router-dom'
import { 
  HiHome, 
  HiFolder, 
  HiClipboardList, 
  HiUserGroup, 
  HiDocumentText,
  HiCog,
  HiPlus,
  HiChevronDown,
  HiChevronRight,
  HiChat
} from 'react-icons/hi'
import { useState, useEffect } from 'react'
import { useProjects } from '../../hooks/useProjects'
import { useAuth } from '../../hooks/useAuth'
import { departmentsApi } from '../../api/client'

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
    isActive
      ? 'bg-brand-50 text-brand-700 shadow-sm dark:bg-brand-900/30 dark:text-brand-300'
      : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900 active:bg-surface-200 dark:text-surface-400 dark:hover:bg-surface-700 dark:hover:text-surface-200 dark:active:bg-surface-600'
  }`

export default function Sidebar({ collapsed = false, onNavigate }) {
  const location = useLocation()
  const { isAdmin } = useAuth()
  const { projects } = useProjects()
  const [departments, setDepartments] = useState([])
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [departmentsExpanded, setDepartmentsExpanded] = useState(true)

  useEffect(() => {
    async function fetchDepartments() {
      try {
        const response = await departmentsApi.list({ my_departments: true })
        // Ensure we always have an array, even if API returns unexpected data
        setDepartments(Array.isArray(response.data) ? response.data : [])
      } catch (err) {
        console.error('Failed to fetch departments:', err)
      }
    }
    fetchDepartments()
  }, [])

  if (collapsed) {
    return (
      <aside className="w-16 bg-white dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col py-4 shrink-0">
        <nav className="flex flex-col items-center gap-1">
          <NavLink to="/" onClick={onNavigate} aria-label="Dashboard" title="Dashboard" className={({ isActive }) =>
            `p-3 rounded-lg transition-all duration-150 ${isActive ? 'bg-brand-50 text-brand-700 shadow-sm dark:bg-brand-900/30 dark:text-brand-300' : 'text-surface-500 hover:bg-surface-100 hover:text-surface-700 active:bg-surface-200 dark:hover:bg-surface-700 dark:hover:text-surface-300'}`
          }>
            <HiHome className="w-5 h-5" />
          </NavLink>
          <NavLink to="/my-tasks" onClick={onNavigate} aria-label="My Tasks" title="My Tasks" className={({ isActive }) =>
            `p-3 rounded-lg transition-all duration-150 ${isActive ? 'bg-brand-50 text-brand-700 shadow-sm dark:bg-brand-900/30 dark:text-brand-300' : 'text-surface-500 hover:bg-surface-100 hover:text-surface-700 active:bg-surface-200 dark:hover:bg-surface-700 dark:hover:text-surface-300'}`
          }>
            <HiClipboardList className="w-5 h-5" />
          </NavLink>
          <NavLink to="/transcripts" onClick={onNavigate} aria-label="Transcripts" title="Transcripts" className={({ isActive }) =>
            `p-3 rounded-lg transition-all duration-150 ${isActive ? 'bg-brand-50 text-brand-700 shadow-sm dark:bg-brand-900/30 dark:text-brand-300' : 'text-surface-500 hover:bg-surface-100 hover:text-surface-700 active:bg-surface-200 dark:hover:bg-surface-700 dark:hover:text-surface-300'}`
          }>
            <HiDocumentText className="w-5 h-5" />
          </NavLink>
        </nav>
      </aside>
    )
  }

  return (
    <aside className="w-60 bg-white dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col shrink-0 overflow-hidden" data-tour="sidebar">
      <nav className="flex-1 overflow-y-auto p-3">
        {/* Main Navigation */}
        <div className="space-y-1 mb-6">
          <NavLink to="/" onClick={onNavigate} className={navLinkClass}>
            <HiHome className="w-5 h-5" />
            Dashboard
          </NavLink>
          <NavLink to="/my-tasks" onClick={onNavigate} className={navLinkClass}>
            <HiClipboardList className="w-5 h-5" />
            My Tasks
          </NavLink>
          <NavLink to="/ai-chat" onClick={onNavigate} className={navLinkClass}>
            <HiChat className="w-5 h-5" />
            HAL 9000
          </NavLink>
          <NavLink to="/transcripts" onClick={onNavigate} className={navLinkClass}>
            <HiDocumentText className="w-5 h-5" />
            Transcripts
          </NavLink>
          <NavLink to="/settings" onClick={onNavigate} className={navLinkClass} data-tour="settings">
            <HiCog className="w-5 h-5" />
            Settings
          </NavLink>
        </div>

        {/* Projects Section */}
        <div className="mb-6" data-tour="projects-section">
          <button
            onClick={() => setProjectsExpanded(!projectsExpanded)}
            className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-surface-500 uppercase tracking-wider hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all duration-150"
            aria-expanded={projectsExpanded}
            aria-controls="sidebar-projects"
          >
            <span className="flex items-center gap-2">
              <HiFolder className="w-4 h-4" />
              Projects
            </span>
            {projectsExpanded ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
          </button>
          
          {projectsExpanded && (
            <div id="sidebar-projects" className="mt-1 space-y-0.5">
              {projects.slice(0, 10).map(project => (
                <NavLink
                  key={project.id}
                  to={`/projects/${project.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all duration-150 ${
                      isActive
                        ? 'bg-brand-50 text-brand-700 shadow-sm dark:bg-brand-900/30 dark:text-brand-300'
                        : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-surface-700 dark:hover:text-surface-200'
                    }`
                  }
                >
                  <span 
                    className="w-2 h-2 rounded-full shrink-0 ring-2 ring-white dark:ring-surface-800 shadow-sm"
                    style={{ backgroundColor: project.color || '#3b82f6' }}
                  />
                  <span className="truncate">{project.name}</span>
                </NavLink>
              ))}
              {projects.length > 10 && (
                <NavLink
                  to="/"
                  onClick={onNavigate}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg"
                >
                  View all ({projects.length})
                </NavLink>
              )}
            </div>
          )}
        </div>

        {/* Departments Section */}
        <div className="mb-6">
          <button
            onClick={() => setDepartmentsExpanded(!departmentsExpanded)}
            className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-surface-500 uppercase tracking-wider hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg transition-all duration-150"
            aria-expanded={departmentsExpanded}
            aria-controls="sidebar-departments"
          >
            <span className="flex items-center gap-2">
              <HiUserGroup className="w-4 h-4" />
              Departments
            </span>
            {departmentsExpanded ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
          </button>
          
          {departmentsExpanded && (
            <div id="sidebar-departments" className="mt-1 space-y-1">
              {departments.map(dept => (
                <NavLink
                  key={dept.id}
                  to={`/departments/${dept.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                      isActive
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                        : 'text-surface-600 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-700'
                    }`
                  }
                >
                  <span className="truncate">{dept.name}</span>
                  <span className="text-xs text-surface-400">{dept.member_count || 0}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Admin Section */}
        {isAdmin && (
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-surface-500 uppercase tracking-wider">
              Admin
            </div>
            <NavLink to="/admin" onClick={onNavigate} className={navLinkClass}>
              <HiCog className="w-5 h-5" />
              Settings
            </NavLink>
          </div>
        )}
      </nav>
    </aside>
  )
}
